import { API_URL, BASE_URL, SERVER_TIMEZONE } from "./constants";
import Noty from "noty";
import "noty/lib/noty.css";
import "noty/lib/themes/mint.css";
import 'animate.css/animate.min.css';
import dayjs from "dayjs";
import moment from 'moment-timezone';
import { lazy } from "react";
import { store } from "redux/store";
import { resetAuthState } from "redux/authSlice";

export const getAPIURL = (url = '') => {
    return String(url).substring(0, API_URL.length) !== API_URL ? `${API_URL}${url}` : url;
};
export const getBaseURL = (url = '') => {
    return String(url).substring(0, BASE_URL.length) !== BASE_URL ? `${BASE_URL}${url}` : url;
};


export const isEmpty = (obj) => {
    for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
            return false;
        }
    }

    return true;
}
export const isEmptyObject = (value) => {
    if (value == null) {
        // null or undefined
        return false;
    }
    if (typeof value != 'object') {
        // boolean, number, string, function, etc.
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    // consider `Object.create(null)`, commonly used as a safe map
    // before `Map` support, an empty object as well as `{}`
    if (proto != null && proto != Object.prototype) {
        return false;
    }

    return isEmpty(value);
}

export const getAuthToken = () => {
    const state = store.getState();
    return state.auth.data?.accessToken;
}

export const getFile = (url = '') => {
    let options = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Accept': 'application/json',
        },
    };
    return fetch(url, options);
}

export const sendAPIRequest = (url, method = 'GET', body = '', additionalHeaders = {}, ACObj = null) => {
    let contentType = {};
    if (body != '' && typeof body == 'object' && !(body instanceof FormData)) {
        contentType = { 'Content-Type': 'application/json' };
    }
    let options = {
        method,
        headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Accept': 'application/json',
            ...(body != '' ? contentType : {}),
            ...additionalHeaders
        },
    };
    if (body != '') {
        if (body instanceof FormData) {
            options.body = body;
        } else if (typeof body == 'object') {
            options.body = JSON.stringify(body);
        } else {
            options.body = body;
        }
    }
    if (ACObj) {
        options.signal = ACObj;
    }
    const URL = getAPIURL(`/api${url}`);
    return fetch(URL, options);
}

export const handleRequest = async (url, method = 'GET', body = '', additionalHeaders = {}, ACObj = null) => {
    const ret = await sendAPIRequest(url, method, body, additionalHeaders, ACObj);
    const res = await ret.json();
    if (res.success && res.status === 401 && res.error?.error_code === 'AUTH_ERROR') {
        displayNotification("error", "Session expired, please login again");
        displayNotification("info", "Navigating to login page...");
        setTimeout(() => {
            store.dispatch(resetAuthState());
            window.location.href = '/login';
        }, 2000);
    }
    return res;
}

export const displayNotification = (type, text, layout = "bottomRight") => {
    let animation = {
        open: 'animate__animated animate__fadeInRight', // Animate.css class names
        close: 'animate__animated animate__fadeOutRight' // Animate.css class names
    };
    if (layout == "bottomRight") {
        animation = {
            open: 'animate__animated animate__slideInRight',
            close: 'animate__animated animate__slideOutRight'
        }
    }
    const noty = new Noty({
        type,
        text,
        layout,
        // theme: 'bootstrap-v5',
        timeout: 1500,
        animation
    });
    // noty.setTimeout(1500);
    noty.show();
}

export const generateOptionsFromData = (data, mappings = { value: 'value', label: 'label' }, useKeyValue = false) => {
    let ret = [];
    if (useKeyValue) {
        for (const [value, label] of Object.entries(data)) {
            ret.push({ value, label });
        }
    } else {
        for (const r of data) {
            ret.push({
                value: r[mappings.value],
                label: typeof mappings.label != 'string'
                    ? r[mappings.label[0]] ? r[mappings.label[0]][mappings.label[1]] : ""
                    : r[mappings.label]
            });
        }
    }
    return ret;
}

export const toggleValueInArray = (arr = [], val = '') => {
    if (arr.includes(val)) {
        return arr.filter(v => v != val);
    } else {
        return [...arr, val];
    }
}

export const getFormattedDate = (val, format = "YYYY-MM-DD", defaultValue = '', timezone = null) => {
    const userTimeZone = timezone || getUserTimeZone();
    if (val != null) {
        if (moment.isMoment(val) || dayjs.isDayjs(val)) {
            return val.tz(userTimeZone, SERVER_TIMEZONE).format(format);
        } else {
            return moment.utc(val).tz(userTimeZone, SERVER_TIMEZONE).format(format);
        }
    } else {
        return defaultValue;
    }
}

export const lazyRetry = function (componentImport, name) {
    return new Promise((resolve, reject) => {
        // check if the window has already been refreshed
        const hasRefreshed = JSON.parse(
            window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false'
        );
        // try to import the component
        componentImport().then((component) => {
            window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false'); // success so reset the refresh
            resolve(component);
        }).catch((error) => {
            if (!hasRefreshed) { // not been refreshed yet
                window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true'); // we are now going to refresh
                return window.location.reload(); // refresh the page
            }
            reject(error); // Default error behaviour as already tried refresh
        });
    });
};

export const reactLazyImport = (path, name) => {
    return lazy(() => lazyRetry(() => import(`.${path}`), name));
}

export const getListElement = (item) => {
    return item ? item.list_element_name : '';
}

export const renderBoolean = (value) => ['', null, undefined].includes(value) ? 'N/A' : [true, 1].includes(value) ? 'Yes' : 'No';

export const getUserTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}